import type { CameraSettings, Difficulty, UpgradeKind, WeaponConfig, WeaponKind } from './types';

export const CELL_SIZE = 2;
export const GRID_COLS = 14;
export const GRID_ROWS = 14;
export const BASE_COL = Math.floor((GRID_COLS - 2) / 2);
export const BASE_ROW = GRID_ROWS - 3;
export const BASE_COLS = 2;
export const BASE_ROWS = 2;
export const GRID_STEP_TIME = 0.18;
export const ENEMY_BASE_RANGE = 3.85;
export const ENEMY_PLAYER_RANGE = 7.5;
export const ENEMY_AGGRO_TIME = 4;
/** Seconds spent crossing one grid cell on the default difficulty. */
export const ENEMY_SCOUT_MOVE_TIME = 0.42;
export const ENEMY_HEAVY_MOVE_TIME = 0.56;
export const ENEMY_LAUNCHER_MOVE_TIME = 0.7;
export const ENEMY_BULLDOZER_MOVE_TIME = 0.82;
export const BASE_SHIELD_DURATION = 14;
export const MAX_LEVEL = 6;
export const DROP_MIN_INTERVAL = 10;
export const DROP_MAX_INTERVAL = 17;
export const DROP_DESCENT_TIME = 2.6;
export const DROP_LIFETIME = 28;
export const WAVE_INTERMISSION_TIME = 3;

const FIELD_WIDTH = GRID_COLS * CELL_SIZE;
const FIELD_DEPTH = GRID_ROWS * CELL_SIZE;

export const FIELD = {
  width: FIELD_WIDTH,
  depth: FIELD_DEPTH,
  left: -FIELD_WIDTH / 2,
  right: FIELD_WIDTH / 2,
  top: -FIELD_DEPTH / 2,
  bottom: FIELD_DEPTH / 2,
  spawnZ: -FIELD_DEPTH / 2 + CELL_SIZE / 2,
  baseX: -FIELD_WIDTH / 2 + (BASE_COL + BASE_COLS / 2) * CELL_SIZE,
  baseZ: -FIELD_DEPTH / 2 + (BASE_ROW + BASE_ROWS / 2) * CELL_SIZE,
} as const;

export const WEAPONS: Record<WeaponKind, WeaponConfig> = {
  electro: { name: 'E-MITTER // 01', display: 'ЭЛЕКТРОМАГНИТНАЯ', damage: 1.4, speed: 18, cooldown: 0.39, range: 'LONG', color: 0x69edf2, reward: 0, kind: 'electro' },
  flame: { name: 'PYRO STREAM // 02', display: 'ОГНЕМЁТ', damage: 0.55, speed: 12, cooldown: 0.14, range: 'CLOSE', color: 0xff8b4e, reward: 180, kind: 'flame' },
  rocket: { name: 'ROCKET POD // 03', display: 'РАКЕТНЫЙ БЛОК', damage: 4.2, speed: 10, cooldown: 0.92, range: 'BLAST', color: 0xb889f2, reward: 260, kind: 'rocket' },
};

export const UPGRADE_BASE_COSTS: Record<UpgradeKind, number> = { power: 120, cooling: 140, shield: 160 };
export const UPGRADE_KINDS: UpgradeKind[] = ['power', 'cooling', 'shield'];

export const PALETTE = {
  cyan: 0x65edf2,
  cyanDark: 0x1a7580,
  orange: 0xf49a55,
  red: 0xef6d67,
  green: 0x71c47e,
  greenDark: 0x244a31,
  enemyBlue: 0x315d7a,
  enemySteel: 0x35424c,
  ground: 0x0a151b,
} as const;

export const CAMERA_SETTINGS: CameraSettings = { tilt: 50, distance: 26, fov: 48, lookAhead: -1 };

export interface DifficultyConfig {
  label: string;
  enemyCountMultiplier: number;
  enemyMoveTimeMultiplier: number;
  enemyReactionDelay: number;
  enemyTurretTurnMultiplier: number;
  enemyFireCooldownMultiplier: number;
  enemyProjectileSpeedMultiplier: number;
}

/** Tunings are intentionally explicit so each difficulty remains easy to audit. */
export const DIFFICULTY_CONFIG: Record<Difficulty, DifficultyConfig> = {
  easy: {
    label: 'EASY',
    enemyCountMultiplier: 0.65,
    enemyMoveTimeMultiplier: 1.55,
    enemyReactionDelay: 0.9,
    enemyTurretTurnMultiplier: 0.7,
    enemyFireCooldownMultiplier: 1.3,
    enemyProjectileSpeedMultiplier: 0.82,
  },
  normal: {
    label: 'NORMAL',
    enemyCountMultiplier: 1,
    enemyMoveTimeMultiplier: 1,
    enemyReactionDelay: 0.45,
    enemyTurretTurnMultiplier: 1,
    enemyFireCooldownMultiplier: 1,
    enemyProjectileSpeedMultiplier: 1,
  },
  hard: {
    label: 'HARD',
    enemyCountMultiplier: 1.25,
    enemyMoveTimeMultiplier: 0.85,
    enemyReactionDelay: 0.22,
    enemyTurretTurnMultiplier: 1.15,
    enemyFireCooldownMultiplier: 0.88,
    enemyProjectileSpeedMultiplier: 1.1,
  },
  extreme: {
    label: 'EXTREME',
    enemyCountMultiplier: 1.55,
    enemyMoveTimeMultiplier: 0.7,
    enemyReactionDelay: 0.08,
    enemyTurretTurnMultiplier: 1.3,
    enemyFireCooldownMultiplier: 0.74,
    enemyProjectileSpeedMultiplier: 1.22,
  },
};
